/**
 * PatientReferralAdapter - Adapter for ReferralDomain to PatientReferralApiService interface
 *
 * Provides backward compatibility by wrapping ReferralDomain methods
 * to match the existing PatientReferralApiService interface.
 * Handles parameter transformation, response mapping, and client-side filtering
 * for features not supported by the new ReferralDomain.
 *
 * @module services/adapters/PatientReferralAdapter
 */

import { apiService } from '@/services/ApiService';
import type { ReferralDomain, ReferralApiError, ReferralStatus } from '@/services/domains/ReferralDomain';
import type {
  Referral,
  ListReferralsRequest,
  ListReferralsResponse,
} from '@/services/domains/ReferralDomain';
import type {
  PatientReferralData,
  PatientReferralsParams,
  PatientReferralsResponse,
  PatientReferralApiError,
  PatientReferralStatus,
} from '@/types/patientDashboard';
// Create PatientReferralApiError class since the original file was removed
class PatientReferralApiErrorClass extends Error {
  public status?: number;
  public fieldErrors?: Record<string, string>;
  public code?: string;

  constructor(
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: string
  ) {
    super(message);
    this.name = 'PatientReferralApiError';
    this.status = status;
    this.fieldErrors = fieldErrors;
    this.code = code;
  }
}

/**
 * Interface for PatientReferralAdapter to ensure compatibility
 */
export interface PatientReferralAdapterInterface {
  getPatientReferrals(params?: PatientReferralsParams): Promise<PatientReferralsResponse>;
  getPatientReferral(referralId: string): Promise<PatientReferralData>;
  getPatientReferralByToken(token: string): Promise<PatientReferralData>;
  setAuthToken(token: string): void;
  clearAuthToken(): void;
  healthCheck(): Promise<{ status: string; timestamp: string }>;
}

/**
 * PatientReferralAdapter class
 */
export class PatientReferralAdapter implements PatientReferralAdapterInterface {
  private referralDomain: ReferralDomain;

  constructor() {
    this.referralDomain = apiService.referral;
  }

  /**
   * Transform PatientReferralsParams to ListReferralsRequest
   */
  private transformParamsToListRequest(params: PatientReferralsParams): ListReferralsRequest {
    const request: ListReferralsRequest = {};

    // Handle pagination transformation (page/perPage → offset/limit)
    if (params.page && params.perPage) {
      request.offset = (params.page - 1) * params.perPage;
      request.limit = params.perPage;
    } else if (params.perPage) {
      request.limit = params.perPage;
    }

    // Direct mappings
    if (params.status && params.status !== 'all') {
      // Map PatientReferralStatus to ReferralStatus
      const statusMap: Record<string, ReferralStatus> = {
        'pending': 'pending',
        'claimed': 'claimed',
        'scheduled': 'claimed', // Map to claimed as closest equivalent
        'no_show': 'claimed',
        'awaiting_mealplan': 'claimed',
        'complete': 'completed',
        'expired': 'expired',
        'cancelled': 'cancelled'
      };
      request.status = statusMap[params.status] || 'pending';
    }

    if (params.referringPhysicianId) {
      request.providerId = params.referringPhysicianId;
    }

    // Sort field mapping
    if (params.sortBy) {
      const sortMap: Record<string, 'createdAt' | 'updatedAt' | 'expiresAt'> = {
        'createdAt': 'createdAt',
        'updatedAt': 'updatedAt',
        'expiresAt': 'expiresAt',
        'patientName': 'createdAt', // Fallback to createdAt
        'status': 'createdAt' // Fallback to createdAt
      };
      request.sortBy = sortMap[params.sortBy] || 'createdAt';
    }

    if (params.sortOrder) {
      request.sortOrder = params.sortOrder;
    }

    return request;
  }

  /**
   * Transform Referral to PatientReferralData
   */
  private transformReferralToPatientData(referral: Referral): PatientReferralData {
    return {
      id: referral.id,
      referralToken: referral.referral_token,
      referringPhysicianId: referral.referring_physician_id,
      referringPhysicianName: referral.referring_physician_name,
      referringPhysicianEmail: referral.referring_physician_email || '',
      referringPhysicianRole: referral.referring_physician_role || '',
      dietitianId: referral.dietitian_id || '',
      dietitianName: referral.dietitian_name || '',
      dietitianEmail: referral.dietitian_email || '',
      dietitianSchedulingUri: referral.dietitian_scheduling_uri || '',
      patientName: referral.patient_name || '',
      dateOfBirth: referral.date_of_birth || '',
      patientIdNumber: referral.patient_id_number || '',
      patientIdType: referral.patient_id_type || '',
      phone: referral.phone || '',
      email: referral.email || referral.patient_email || '',
      conditions: referral.conditions || [],
      additionalNotes: referral.additional_notes || null,
      status: this.mapReferralStatusToPatientStatus(referral.status),
      claimedByUserId: referral.claimed_by_user_id || referral.patient_id || null,
      claimedAt: referral.claimed_at || null,
      expiresAt: referral.expires_at,
      createdAt: referral.created_at,
      updatedAt: referral.updated_at
    };
  }

  /**
   * Map ReferralStatus to PatientReferralStatus
   */
  private mapReferralStatusToPatientStatus(status: string): PatientReferralStatus {
    const statusMap: Record<string, PatientReferralStatus> = {
      'pending': 'pending',
      'claimed': 'claimed',
      'scheduled': 'claimed',
      'completed': 'complete',
      'expired': 'expired',
      'cancelled': 'cancelled'
    };
    return statusMap[status] || 'pending';
  }

  /**
   * Transform ListReferralsResponse to PatientReferralsResponse
   */
  private transformListResponseToPaginated(
    response: ListReferralsResponse,
    originalParams: PatientReferralsParams
  ): PatientReferralsResponse {
    // Transform referrals
    let transformedReferrals = response.referrals.map(referral =>
      this.transformReferralToPatientData(referral)
    );

    // Apply client-side filtering for unsupported features
    if (originalParams.search) {
      transformedReferrals = this.applySearchFilter(transformedReferrals, originalParams.search);
    }

    if (originalParams.dateFrom || originalParams.dateTo) {
      transformedReferrals = this.applyDateFilter(
        transformedReferrals,
        originalParams.dateFrom,
        originalParams.dateTo
      );
    }

    // Calculate pagination based on original params
    const page = originalParams.page || 1;
    const perPage = originalParams.perPage || response.per_page;
    const totalPages = Math.ceil(response.total / perPage);

    return {
      referrals: transformedReferrals,
      pagination: {
        page: page,
        perPage: perPage,
        total: response.total,
        totalPages: totalPages
      }
    };
  }

  /**
   * Apply client-side search filtering
   */
  private applySearchFilter(referrals: PatientReferralData[], searchTerm: string): PatientReferralData[] {
    const lowerSearch = searchTerm.toLowerCase();

    return referrals.filter(referral => {
      return referral.patientName.toLowerCase().includes(lowerSearch) ||
             referral.referringPhysicianName.toLowerCase().includes(lowerSearch) ||
             referral.email.toLowerCase().includes(lowerSearch) ||
             referral.referralToken.toLowerCase().includes(lowerSearch);
    });
  }

  /**
   * Apply client-side date filtering
   */
  private applyDateFilter(
    referrals: PatientReferralData[],
    dateFrom?: string,
    dateTo?: string
  ): PatientReferralData[] {
    return referrals.filter(referral => {
      const referralDate = new Date(referral.createdAt);

      if (dateFrom && referralDate < new Date(dateFrom)) {
        return false;
      }

      if (dateTo && referralDate > new Date(dateTo)) {
        return false;
      }

      return true;
    });
  }

  /**
   * Transform ReferralApiError to PatientReferralApiError
   */
  private transformError(error: ReferralApiError): PatientReferralApiError {
    return new PatientReferralApiErrorClass(
      error.message,
      error.status,
      error.fieldErrors,
      error.code
    );
  }

  /**
   * Get paginated patient referrals (main method)
   */
  async getPatientReferrals(params: PatientReferralsParams = {}): Promise<PatientReferralsResponse> {
    try {
      // Transform parameters
      const listRequest = this.transformParamsToListRequest(params);

      // Call ReferralDomain
      const response = await this.referralDomain.listReferrals(listRequest);

      // Transform response
      return this.transformListResponseToPaginated(response, params);

    } catch (error) {
      if (error && typeof error === 'object' && 'name' in error && error.name === 'ReferralApiError') {
        throw this.transformError(error as ReferralApiError);
      } else {
        throw new PatientReferralApiErrorClass(
          error instanceof Error ? error.message : 'Failed to fetch patient referrals',
          undefined,
          undefined,
          'FETCH_ERROR'
        );
      }
    }
  }

  /**
   * Get single patient referral by ID
   */
  async getPatientReferral(referralId: string): Promise<PatientReferralData> {
    try {
      const referral = await this.referralDomain.getReferral(referralId);
      return this.transformReferralToPatientData(referral);

    } catch (error) {
      if (error && typeof error === 'object' && 'name' in error && error.name === 'ReferralApiError') {
        throw this.transformError(error as ReferralApiError);
      } else {
        throw new PatientReferralApiErrorClass(
          error instanceof Error ? error.message : 'Failed to fetch patient referral',
          undefined,
          undefined,
          'FETCH_ERROR'
        );
      }
    }
  }

  /**
   * Get patient referral by token
   */
  async getPatientReferralByToken(token: string): Promise<PatientReferralData> {
    try {
      const referral = await this.referralDomain.getReferralByToken(token);
      return this.transformReferralToPatientData(referral);

    } catch (error) {
      if (error && typeof error === 'object' && 'name' in error && error.name === 'ReferralApiError') {
        throw this.transformError(error as ReferralApiError);
      } else {
        throw new PatientReferralApiErrorClass(
          error instanceof Error ? error.message : 'Failed to fetch patient referral by token',
          undefined,
          undefined,
          'FETCH_ERROR'
        );
      }
    }
  }

  /**
   * Set authentication token
   */
  setAuthToken(token: string): void {
    // ApiService handles token management globally
    apiService.setAuthToken(token);
  }

  /**
   * Clear authentication token
   */
  clearAuthToken(): void {
    // ApiService handles token management globally
    apiService.clearAuthToken();
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    try {
      const health = await apiService.healthCheck();
      return {
        status: health.status,
        timestamp: health.timestamp
      };
    } catch {
      throw new PatientReferralApiErrorClass(
        'Health check failed',
        undefined,
        undefined,
        'HEALTH_CHECK_ERROR'
      );
    }
  }
}

// Export singleton instance
export const patientReferralAdapter = new PatientReferralAdapter();
export default PatientReferralAdapter;